import React, {useEffect, useState} from "react";
import {Link as RouterLink, useLocation} from "react-router-dom";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Container,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerOverlay,
    HStack,
    IconButton,
    Link,
    Text,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import {ChevronDownIcon, HamburgerIcon, PhoneIcon} from "@chakra-ui/icons";
import {categories, categoryUrl, subCategoryUrl} from "../data/catalog";
import {site} from "../data/site";
import {useQuote} from "./QuoteContext";
import Logo from "./Logo";

export const navLinks = [
    {label: "Services", to: "/#services"},
    {label: "Gallery", to: "/gallery"},
    {label: "How it works", to: "/#process"},
    {label: "About", to: "/#about"},
    {label: "Contact", to: "/#contact"},
];

const navItemStyle = {
    px: {lg: 3, xl: 4},
    h: "100%",
    display: "flex",
    alignItems: "center",
    gap: 1,
    fontSize: "sm",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ink.800",
    position: "relative",
    whiteSpace: "nowrap",
    _after: {
        content: '""',
        position: "absolute",
        left: {lg: 3, xl: 4},
        right: {lg: 3, xl: 4},
        bottom: 0,
        h: "3px",
        bg: "brand.500",
        transform: "scaleX(0)",
        transition: "transform 0.2s",
    },
    _hover: {textDecoration: "none", color: "brand.500", _after: {transform: "scaleX(1)"}},
};

function MegaMenu({onNavigate}) {
    return (
        <Box position="absolute" top="100%" left={0} right={0} bg="white" boxShadow="0 18px 40px rgba(0,0,0,0.12)"
             borderTop="1px solid" borderColor="ink.100">
            <Container maxW="container.xl" py={10}>
                <Box sx={{columnCount: 4, columnGap: "48px"}}>
                    {categories.map((category) => (
                        <Box key={category.path} mb={7} sx={{breakInside: "avoid"}}>
                            <Link as={RouterLink} to={categoryUrl(category)} onClick={onNavigate}
                                  fontFamily="heading" fontWeight={700} textTransform="uppercase" letterSpacing="0.06em"
                                  fontSize="sm" color="ink.900" _hover={{color: "brand.500", textDecoration: "none"}}>
                                {category.title}
                            </Link>
                            <VStack align="start" spacing={1.5} mt={2.5}>
                                {category.content.map((sub) => (
                                    <Link key={sub.path} as={RouterLink} to={subCategoryUrl(category, sub)} onClick={onNavigate}
                                          fontSize="sm" color="ink.500" lineHeight={1.4}
                                          _hover={{color: "brand.500", textDecoration: "none"}}>
                                        {sub.description}
                                    </Link>
                                ))}
                            </VStack>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
}

function Header() {
    const {openQuote} = useQuote();
    const drawer = useDisclosure();
    const closeDrawer = drawer.onClose;
    const [megaOpen, setMegaOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMegaOpen(false);
        closeDrawer();
    }, [location.key, closeDrawer]);

    useEffect(() => {
        if (!megaOpen) return undefined;
        const onKey = (e) => e.key === "Escape" && setMegaOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [megaOpen]);

    return (
        <>
            {/* Top bar */}
            <Box bg="ink.900" color="whiteAlpha.800" fontSize="xs" letterSpacing="0.04em">
                <Container maxW="container.xl" display="flex" justifyContent={{base: "center", md: "space-between"}} py={2}>
                    <Text display={{base: "none", md: "block"}}>
                        Qualified & licensed carpenter · Licence No. {site.license} · ABN {site.abn}
                    </Text>
                    <Link href={site.phoneHref} _hover={{color: "white"}}>
                        Free quote: <Text as="span" fontWeight={700} color="white">{site.phone}</Text>
                    </Link>
                </Container>
            </Box>

            {/* Main header */}
            <Box as="header" position="sticky" top={0} zIndex="sticky" bg="white"
                 borderBottom="1px solid" borderColor="ink.100" onMouseLeave={() => setMegaOpen(false)}>
                <Container maxW="container.xl" h={{base: "68px", lg: "84px"}} display="flex" alignItems="center" gap={{base: 3, xl: 6}}>
                    <Link as={RouterLink} to="/" flexShrink={0} aria-label={`${site.name} home`} _hover={{textDecoration: "none"}}>
                        <Logo/>
                    </Link>

                    <HStack as="nav" spacing={0} flex={1} h="100%" justify="center" display={{base: "none", lg: "flex"}}>
                        <Box as="button" type="button" {...navItemStyle} color={megaOpen ? "brand.500" : "ink.800"}
                             aria-expanded={megaOpen} aria-haspopup="true"
                             onMouseEnter={() => setMegaOpen(true)} onClick={() => setMegaOpen((open) => !open)}>
                            Products
                            <ChevronDownIcon boxSize={4} transition="transform 0.2s" transform={megaOpen ? "rotate(180deg)" : "none"}/>
                        </Box>
                        {navLinks.map((item) => (
                            <Link key={item.label} as={RouterLink} to={item.to} {...navItemStyle}
                                  color={location.pathname === item.to ? "brand.500" : "ink.800"}
                                  onMouseEnter={() => setMegaOpen(false)}>
                                {item.label}
                            </Link>
                        ))}
                    </HStack>

                    <HStack spacing={5} display={{base: "none", lg: "flex"}} flexShrink={0}>
                        <Link href={site.phoneHref} display={{base: "none", xl: "flex"}} alignItems="center" gap={2}
                              fontWeight={700} color="ink.900" _hover={{color: "brand.500", textDecoration: "none"}}>
                            <PhoneIcon color="brand.500"/> {site.phone}
                        </Link>
                        <Button onClick={() => openQuote()} size="md" px={5}>Free quote</Button>
                    </HStack>

                    <HStack spacing={1} ml="auto" display={{base: "flex", lg: "none"}}>
                        <IconButton as="a" href={site.phoneHref} aria-label="Call us" icon={<PhoneIcon/>} variant="ghost" colorScheme="gray"/>
                        <IconButton aria-label="Open menu" icon={<HamburgerIcon boxSize={6}/>} variant="ghost" colorScheme="gray"
                                    onClick={drawer.onOpen}/>
                    </HStack>
                </Container>

                {megaOpen && <MegaMenu onNavigate={() => setMegaOpen(false)}/>}
            </Box>

            {/* Mobile menu */}
            <Drawer isOpen={drawer.isOpen} onClose={drawer.onClose} placement="right" size="sm">
                <DrawerOverlay/>
                <DrawerContent>
                    <DrawerCloseButton top={4}/>
                    <Box px={6} py={4} borderBottom="1px solid" borderColor="ink.100">
                        <Logo size="40px"/>
                    </Box>
                    <DrawerBody px={0}>
                        <Accordion allowToggle>
                            <AccordionItem borderTop={0}>
                                <AccordionButton px={6} py={4} fontWeight={600} textTransform="uppercase" letterSpacing="0.08em" fontSize="sm">
                                    <Box flex={1} textAlign="left">Products</Box>
                                    <AccordionIcon/>
                                </AccordionButton>
                                <AccordionPanel px={6} pb={4} bg="ink.50">
                                    <VStack align="stretch" spacing={0}>
                                        {categories.map((category) => (
                                            <Link key={category.path} as={RouterLink} to={categoryUrl(category)} py={2.5}
                                                  fontSize="sm" color="ink.700" _hover={{color: "brand.500"}}>
                                                {category.title}
                                            </Link>
                                        ))}
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                        <VStack align="stretch" spacing={0}>
                            {navLinks.map((item) => (
                                <Link key={item.label} as={RouterLink} to={item.to} px={6} py={4}
                                      borderBottom="1px solid" borderColor="ink.100"
                                      fontWeight={600} textTransform="uppercase" letterSpacing="0.08em" fontSize="sm"
                                      _hover={{color: "brand.500", textDecoration: "none"}}>
                                    {item.label}
                                </Link>
                            ))}
                        </VStack>
                    </DrawerBody>
                    <DrawerFooter flexDirection="column" alignItems="stretch" gap={3} borderTop="1px solid" borderColor="ink.100">
                        <Button as="a" href={site.phoneHref} variant="outline" colorScheme="gray" leftIcon={<PhoneIcon/>}>
                            {site.phone}
                        </Button>
                        <Button onClick={() => { drawer.onClose(); openQuote(); }}>Book a free quote</Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
        </>
    );
}

export default Header;
